import { css, Global } from '@emotion/react';
import { memo, type ReactNode, useContext, useMemo } from 'react';

import { useMemoizedBundle } from '@didaskill/ui/utils';

import { LayersContext } from '../layers-provider/LayersContext';
import { DIALOG_LAYER_TYPE, MODAL_ROOT_ID } from './constants';
import { type DialogLayer, DialogsContext } from './DialogsContext';

export type DialogsProviderProps = {
  /** CSS selector of the root element. */
  rootElementSelector: string;
  /** App. */
  children?: ReactNode;
};

export const DialogsProviderBase = ({
  rootElementSelector,
  children = null,
}: DialogsProviderProps) => {
  const { layers } = useContext(LayersContext);

  const dialogLayers = useMemo(
    () => layers.filter(({ type }) => type === DIALOG_LAYER_TYPE) as DialogLayer[],
    [layers],
  );

  const contextValue = useMemoizedBundle({
    dialogLayers,
  });

  // Init the modal root at the bottom of the body.
  if (!document.getElementById(MODAL_ROOT_ID)) {
    const modalRoot = document.createElement('div');
    modalRoot.setAttribute('id', MODAL_ROOT_ID);
    document.body.appendChild(modalRoot);
  }

  return (
    <DialogsContext.Provider value={contextValue}>
      <Global
        styles={css`
          ${rootElementSelector} {
            filter: ${dialogLayers.length > 0 ? `blur(${14 * dialogLayers.length}px)` : 'none'};
            overflow: ${dialogLayers.length > 0 ? 'hidden' : 'initial'};
          }
        `}
      />

      {children}
    </DialogsContext.Provider>
  );
};

DialogsProviderBase.displayName = 'DialogsProvider';

export const DialogsProvider = memo(DialogsProviderBase);
